import { Button, Form, ForwardedInput, Label } from 'hatch-components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useConnect } from 'redux-bundler-hook';
import DrawerBody from 'root/components/molecules/Drawer/components/DrawerBody';
import DrawerError from 'root/components/molecules/Drawer/components/DrawerError';
import DrawerFooter from 'root/components/molecules/Drawer/components/DrawerFooter';
import FormMaskedInput from 'root/components/molecules/FormMaskedInput';
import { FormSelect } from 'root/components/molecules/FormSelect';
import formatPhoneNumber from 'utils/formatPhoneNumber';
import isCorrectHatchIdFormat from 'utils/isCorrectHatchIdFormat';
import telephoneInputMask from 'utils/telephoneInputMask';

const ROLES = [
  { label: 'Manager', value: 'manager' },
  { label: 'User', value: 'user' },
];

const EditTeamMemberForm = ({ userId, onClose, form }) => {
  const [initialized, setInitialized] = useState(false);
  const { register, control, errors, handleSubmit, reset, formState, watch } =
    form;
  const {
    boardOptions,
    currentOrganizationId,
    doCreateNotificationMessage,
    doUpdateTeamMember,
    freshCurrentOrganizationBoards,
    freshCurrentOrganizationTeam,
    isUpdatingTeamMember,
    teamMembersById,
    loggedInUserId,
  } = useConnect(
    'doCreateNotificationMessage',
    'doUpdateTeamMember',
    'selectBoardOptions',
    'selectCurrentOrganizationId',
    'selectFreshCurrentOrganizationBoards',
    'selectFreshCurrentOrganizationTeam',
    'selectIsUpdatingTeamMember',
    'selectLoggedInUserId',
    'selectTeamMembersById',
  );
  const { isDirty } = formState;
  const disabled = !freshCurrentOrganizationTeam || isUpdatingTeamMember;
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');

  // prefill form with values from store
  useEffect(() => {
    if (
      freshCurrentOrganizationTeam &&
      freshCurrentOrganizationBoards &&
      !initialized &&
      isCorrectHatchIdFormat(userId)
    ) {
      const user = teamMembersById?.[userId] || {};
      const {
        email,
        firstName,
        lastName,
        phoneNumber,
        assignedBoardIDs,
        role,
        disabled: userDisabled,
      } = user;

      if (userDisabled) {
        return onClose({ ignore: true });
      }

      reset({
        assignedBoardIDs: boardOptions.filter((option) =>
          (assignedBoardIDs || []).includes(option.value),
        ),
        email,
        firstName,
        lastName,
        phoneNumber: formatPhoneNumber(phoneNumber),
        role: ROLES.find(
          ({ value }) => value?.toLowerCase() === role?.toLowerCase(),
        ),
      });
      setInitialized(true);
    }
    return null;
  }, [
    freshCurrentOrganizationTeam,
    freshCurrentOrganizationBoards,
    initialized,
    teamMembersById,
    userId,
    boardOptions,
    reset,
    onClose,
  ]);

  const onDone = (error) => {
    if (error) {
      // TODO: map errors to form
      setSubmitErrorMessage(error?.message);
      return;
    }

    doCreateNotificationMessage({
      message: `Successfully updated user.`,
      type: 'success',
    });
    onClose({ ignore: true });
  };

  const onSubmit = ({ role: { value: roleValue }, assignedBoardIDs }) => {
    // disable form if updating to allow for fetching style of button
    if (isUpdatingTeamMember) return;

    const assignedBoardIDsArray = (assignedBoardIDs || [])
      .map(({ value }) => value)
      .filter((id) => id);

    doUpdateTeamMember(
      {
        assignedBoardIDs: assignedBoardIDsArray,
        organizationId: currentOrganizationId,
        role: roleValue,
        userId,
      },
      onDone,
    );
  };

  const role = watch('role');
  const roleHelperText =
    (role?.value || '').toLowerCase() === 'user'
      ? 'Users only have access to their personal settings, they cannot update workspaces or other members'
      : 'Managers can add or edit members and workspaces within the Account';

  return (
    <>
      {submitErrorMessage ? <DrawerError message={submitErrorMessage} /> : null}
      <DrawerBody>
        <Form disabled={disabled} onSubmit={handleSubmit(onSubmit)}>
          <Label htmlFor="firstName">First Name</Label>
          <ForwardedInput
            data-test-id="15f38bc5-29a7-4916-90ee-346077ca8b80"
            disabled
            fullWidth
            id="firstName"
            name="firstName"
            ref={register}
          />
          <Label htmlFor="lastName">Last Name</Label>
          <ForwardedInput
            data-test-id="ae78eb60-6873-43c5-b757-ec38dfc62e1e"
            disabled
            fullWidth
            id="lastName"
            name="lastName"
            ref={register}
          />
          <Label error={!!errors?.email} htmlFor="email" required>
            Email
          </Label>
          <ForwardedInput
            data-test-id="2f0d573c-ae77-4664-aea3-51e5a59ad5b9"
            disabled
            error={!!errors?.email}
            fullWidth
            helperText={errors?.email?.message}
            id="email"
            name="email"
            ref={register}
          />
          <Label error={!!errors?.phoneNumber} htmlFor="phoneNumber">
            Phone
          </Label>
          <FormMaskedInput
            control={control}
            data-test-id="77ff89c2-33d4-47d9-b46b-b59a3b4e968c"
            disabled
            error={!!errors?.phoneNumber}
            fullWidth
            helperText={errors?.phoneNumber?.message}
            id="phoneNumber"
            maskFn={telephoneInputMask}
            name="phoneNumber"
          />
          <Label error={!!errors?.role} htmlFor="role" required>
            Role
          </Label>
          <FormSelect
            control={control}
            data-test-id="7ba31994-c49b-485f-8e96-ea10f11dd44b"
            error={!!errors?.role}
            fullWidth
            helperText={errors?.role?.message || roleHelperText}
            id="role"
            isDisabled={loggedInUserId === userId}
            name="role"
            options={ROLES}
            rules={{ required: 'A role is required' }}
          />
          <Label htmlFor="assignedBoardIDs">Add/Remove Workspaces</Label>
          <FormSelect
            control={control}
            fullWidth
            id="assignedBoardIDs"
            isMulti
            name="assignedBoardIDs"
            options={boardOptions}
          />
        </Form>
      </DrawerBody>
      <DrawerFooter>
        <Button
          data-test-id="7e99ae9e-8d00-48f4-9ef3-b0b64cfa7aa4"
          disabled={!isDirty}
          fetching={isUpdatingTeamMember}
          minWidth={90}
          onClick={handleSubmit(onSubmit)}
          size="sm"
          variant="primary"
        >
          Save
        </Button>
      </DrawerFooter>
    </>
  );
};

EditTeamMemberForm.propTypes = {
  form: PropTypes.any, // eslint-disable-line
  onClose: PropTypes.func,
  userId: PropTypes.string.isRequired,
};

EditTeamMemberForm.defaultProps = {
  form: {},
  onClose: () => {},
};

export default EditTeamMemberForm;
